// src/store/contactSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { initialState } from '../../../data/setup/eTrackSettings/initialState.eTrackSettings.data';
import {
  fetchEntitelmentSetup as fetchEntitelmentSetupAPI,
  updateEntitelmentSetup as updateEntitelmentSetupAPI,
  fetchSalesImport as fetchSalesImportAPI,
  updateSalesImport as updateSalesImportAPI,
  fetchAccountingGroup as fetchAccountingGroupAPI,
  updateAccountingGroup as updateAccountingGroupAPI,
} from '../../../apis/eTrackSettingAPIs';
import { ACTIONS_E_TRACK_SETTINGS } from '../../../constants/actionCreator';
import {
  handleRejection,
  COMMON_MESSAGE,
} from '../../../constants';
import { createAsyncThunkWrapper } from '../../../services/common/asyncThunkWrapper';

// API Call Functions
export const fetchEntitelmentSetup = createAsyncThunkWrapper(
  ACTIONS_E_TRACK_SETTINGS.FETCH_ENTITEMENT,
  fetchEntitelmentSetupAPI
);

export const updateEntitelmentSetup = createAsyncThunkWrapper(
  ACTIONS_E_TRACK_SETTINGS.UPDATE_ENTITEMENT,
  updateEntitelmentSetupAPI
);

export const fetchSalesImport = createAsyncThunkWrapper(
  ACTIONS_E_TRACK_SETTINGS.FETCH_SALES_IMPORT,
  fetchSalesImportAPI
);

export const updateSalesImport = createAsyncThunkWrapper(
  ACTIONS_E_TRACK_SETTINGS.UPDATE_SALES_IMPORT,
  updateSalesImportAPI
);

export const fetchAccountingGroup = createAsyncThunkWrapper(
  ACTIONS_E_TRACK_SETTINGS.FETCH_ACCOUNTING_GROUP,
  fetchAccountingGroupAPI
);

export const updateAccountingGroup = createAsyncThunkWrapper(
  ACTIONS_E_TRACK_SETTINGS.UPDATE_ACCOUNTING_GROUP,
  updateAccountingGroupAPI
);

const eTrackSettingsSlice = createSlice({
  name: 'eTrackSettings',
  initialState,
  reducers: {
    handleChangeValues: (state, action) => {
      state[action.payload.name] = action.payload.value;
    },
    handleSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
      state.isEntitlementDataSaved = null;
    },
    handleGenerateBillsInAdvancedDays: (state, action) => {
      state.generateBillsInAdvancedDays = action.payload;
    },
    handleSetSalesImportData: (state, action) => {
      state.isSalesImportGridFiltered = true;
      state.salesImportFilterData = action.payload;
      state.saleImportTableData = action.payload;
    },
    handleResetSalesImportFilters: (state, action) => {
      const { data, isOrderGroup } = action.payload;
      state.orderGroupData = data;
      if (!isOrderGroup) {
        state.searchBoxValue.orderGroup = null;
        state.orderGroup = '';
      }
      state.importFlag = COMMON_MESSAGE.select;
      state.deferred = COMMON_MESSAGE.select;
    },
    handleIsdeferredFilterApplied: (state, action) => {
      state.deferredFilterApplied = action.payload;
    },
    handleIsImportFilterApplied: (state, action) => {
      state.importFilterApplied = action.payload;
    },
    handleSalesImportTableData: (state, action) => {
      state.saleImportTableData = action.payload;
    },
    handleSalesImportIsActiveFilterData: (state, action) => {
      state.isSalesImportGridFiltered = true;
      state.salesImportIsActiveFilterData = action.payload;
      state.saleImportTableData = action.payload;
    },
    handleAccountingGroupFliterData: (state, action) => {
      state.accountingGroupFilterData = action.payload;
    },
    handleAccountingGroupCodeData: (state, action) => {
      state.accountGroupCodeData = action.payload;
    },
    handleReset: () => {
      return initialState;
    },
    handleUpdateSalesImportData: (state, action) => {
      state.dataToBeUpdatedForSalesImport = action.payload;
    },
    handleUpdateAccountingGroupData: (state, action) => {
      state.dataToBeUpdatedForAccountingGroup = action.payload;
      state.isSaveAPITriggered = true;
    },
    handleSearchBoxSelection: (state, action) => {
      state.searchBoxValue[action.payload.name] = action.payload.value;
    },
    //loading
    handleLoading: (state, action) => {
      state.loading = action.payload;
    },
    // handle Account Group Row Edit
    handleAccountingGroupRowEdit: (state, action) => {
      state.isAccountingGroupRowEdited = action.payload;
      state.isSaveAPITriggered = false;
      state.isAccountingGroupDataSavedAfterUpdate = false;
    },
    // handle Account Group Data Saved After Update
    handleAccountingGroupDataSavedAfterUpdate: (state, action) => {
      state.isAccountingGroupDataSavedAfterUpdate = action.payload;
    },
    //handle Form Error
    handleError :(state,action)=>{
      state.errors = action?.payload;
    },
    //handle Form Error for eTrack Tab
    handleEtrackTabError :(state, action)=>{
      state.eTrackTabError[action?.payload?.name] = action?.payload ? action?.payload?.value : null;
    }
  },
  extraReducers: (builder) => {
    // fetchEntitelmentSetup
    builder.addCase(fetchEntitelmentSetup.pending, (state) => {
      state.loading = false;
      state.isEntitlementDataFetched = false;
    });
    builder.addCase(fetchEntitelmentSetup.fulfilled, (state, action) => {
      state.loading = false;
      state.eTrackPageData =  action?.payload;
      state.generateBillsInAdvancedDays =
        action?.payload?.generateBillsInAdvancedDays || 0;
      state.autoApproveMoveFlag = action?.payload?.autoApproveMoveFlag;
      state.searchLimitYears = action?.payload?.searchLimitYears;
      state.importLimitYears = action?.payload?.importLimitYears;
      state.isEntitlementDataFetched = true;
    });
    builder.addCase(fetchEntitelmentSetup.rejected, handleRejection);

    // updateEntitelmentSetup
    builder.addCase(updateEntitelmentSetup.pending, (state) => {
      state.isEntitlementDataSaved = null;
      state.isEntitlementDataFetched = true;
      state.loading = true;
    });
    builder.addCase(updateEntitelmentSetup.fulfilled, (state) => {
      state.isEntitlementDataSaved = true;
      state.loading = false;
    });
    builder.addCase(updateEntitelmentSetup.rejected, (state) => {
      state.isEntitlementDataSaved = false;
      state.loading = false;
    });

    // fetchSalesImport
    builder.addCase(fetchSalesImport.pending, (state) => {
      state.isSalesImportResultsFound = false;
    });
    builder.addCase(fetchSalesImport.fulfilled, (state, action) => {
      state.salesImportData = action?.payload ? action?.payload?.map((ele)=> ({...ele, name: ele?.name?.trim()})) : [];
      state.orderGroupNameData = action?.payload ? action?.payload?.map((ele)=> ({...ele, name: ele?.name?.trim()})) : [];
      state.orderGroupData = action?.payload ? action?.payload?.map((ele)=> ({...ele, name: ele?.name?.trim()})) : [];
      state.isSalesImportResultsFound = action?.payload?.length > 0;
      state.loading = false;
    });
    builder.addCase(fetchSalesImport.rejected, handleRejection);

    // updateSalesImport
    builder.addCase(updateSalesImport.pending, (state) => {
      state.isSalesImportDataSaved = false;
    });
    builder.addCase(updateSalesImport.fulfilled, (state) => {
      state.loading = false;
      state.isSalesImportDataSaved = true;
    });
    builder.addCase(updateSalesImport.rejected, handleRejection);

    // fetchAccountingGroup
    builder.addCase(fetchAccountingGroup.pending, (state) => {
      state.isAccountingGroupResultsFound = false;
    });
    builder.addCase(fetchAccountingGroup.fulfilled, (state, action) => {
      const data = action?.payload?.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
      state.accoutingGroupData = data;
      state.accountGroupData = data;
      state.accountGroupCodeData = data;
      state.isAccountingGroupResultsFound = data?.length > 0;
    });
    builder.addCase(fetchAccountingGroup.rejected, (state, action) => {
      handleRejection(state, action);
    });

    // updateAccountingGroup
    builder.addCase(updateAccountingGroup.pending, (state) => {
      state.isAccountingGroupDataSaved = false;
    });
    builder.addCase(updateAccountingGroup.fulfilled, (state) => {
      state.loading = false;
      state.isAccountingGroupDataSaved = true;
    });
    builder.addCase(updateAccountingGroup.rejected, (state, action) => {
      handleRejection(state, action);
    });
  },
});

export const {
  handleSelectedTab,
  handleGenerateBillsInAdvancedDays,
  handleChangeValues,
  handleSetSalesImportData,
  handleSalesImportTableData,
  handleSalesImportIsActiveFilterData,
  handleAccountingGroupFliterData,
  handleAccountingGroupCodeData,
  handleReset,
  handleUpdateSalesImportData,
  handleUpdateAccountingGroupData,
  handleSearchBoxSelection,
  handleResetSalesImportFilters,
  handleIsdeferredFilterApplied,
  handleIsImportFilterApplied,
  handleLoading,
  handleAccountingGroupRowEdit,
  handleAccountingGroupDataSavedAfterUpdate,
  handleError,
  handleEtrackTabError,
} = eTrackSettingsSlice.actions;

export default eTrackSettingsSlice.reducer;
